import React, { useEffect, useState, useRef } from "react";
import { Card, Nav, Tab } from 'react-bootstrap';
import { getContainer } from "../../ioc/IOCSetup";
import { withRouter } from "react-router";
import {
    User} from "../../http/protocol";
import { PagerModel } from "../list/list/PagerModel";
import { DataTable } from "../list/list/DataTable";
import { UserService } from "../../services/UserService";
import { ColumnType } from "../list/list/ColumnType";
import { ConfirmModal } from "../../util/ConfirmModal";
import { UserAuthenticationService } from "../../services/UserAuthenticationService";
import { Form, FormListener } from "../form/Form";
import { FormGroup } from "../form/FormGroup";
import { Submit } from "../form/Submit";
import { FormTextArea } from "../form/FormTextArea";
import { UserProducts } from "./UserProducts";
import { UserAttributes } from "./UserAttributes";
import { UserLabels } from "./UserLabels";
import { BonusAward } from "./UserBonusAward";
import { UserComment } from "./UserComment";
import { UpdateAllAction, UserAction, UpdateActionStatus, UserActionContainer } from "./UserActionContainer";
import { UserDormantCollection } from "./UserDormantCollection";

const WAIT_TIME = 500;

const UsersForm: React.FC<any> = (addUsers: (users: []) => Promise<any>) => {
    const [error, setError] = useState("");
    const [form, setForm] = useState({users:""});
    const [isLoading, setIsLoading] = useState(false);

    function validate(form: any) {
        setError("");
        if (form.users === "") return true;
        let userArray = form.users.toString().trim().split(/[,\n ]+/);
        if (!userArray || userArray.length < 1) {
            setTimeout(() => { setError("No users entered"); }, 100);
            return false;
        }
        //remove empty strings and new lines
        userArray = userArray.filter((item: any) => item !== "");

        //trim all users
        userArray = userArray.map((item: any) => item.trim());

        //if users contains non numbers then set error
        for (let i = 0; i < userArray.length; i++) {
            
            if (!Number(userArray[i].replace(/\D+/g, ''))) {
                setTimeout(() => { 
                    setError("There are non numeric characters"); 
                }, 100);
                return false;
            }
        }
        //if users contains duplicates then get duplicates and add them to the error
        let duplicates = userArray.filter((item: any, index: any) => userArray.indexOf(item) != index);
        if (duplicates.length > 0) {
            setTimeout(() => { setError("Duplicate user ids entered: " + duplicates.join(", ")); }, 100);
            return false;
        }

        return true;
    }

    async function submit() {
        if (!form.users) return;
        if (!validate(form)) return;
        let cleanArray = form.users.toString().split(/[,\n ]+/).map((value: String) => {
            return Number(value.replace(/\D+/g, ''));
        })
        
        if (addUsers){
            setIsLoading(true);
            addUsers(cleanArray).then(() => {
                setIsLoading(false);
                setError("");
            }).catch((error: any) => {
                setError(error.message);
            }).finally(() => {
                setForm({users:""});
                setIsLoading(false);
            });
        }
        
    }

    const ref : FormListener<any> = {
        formDidUpdate: (formModel: any, validationMap: any) => {
            setForm(formModel);
            validate(formModel);
        },
        onSubmit: () => { submit() }
    };

    function renderForm() {
        return (
            <Form className="form-horizontal" formListener={ref} modelObject={form} validationMap={{}}>
                <FormGroup className="row">
                    <div className="col">
                        <FormTextArea className={"form-control col-sm-12"} model="users" placeholder={"Enter your text"} />
                    </div>
                    <div className="col-auto">
                        <Submit className="btn btn-danger" disabled={isLoading} style={{minWidth: "100px"}}>
                            {!isLoading && "Add Users"}
                            {isLoading && <i className="fa fa-spinner fa-spin"></i>}
                        </Submit>
                    </div>
                </FormGroup>
                <FormGroup className="row">
                    <div className="col-sm-12">
                        {!error && <span className={"help-block"}>Enter user ids separated by comma, space or new line</span>}
                        {error && <span style={{ "color": "#f1556c" }} className={"help-block"}>{error}</span>}
                    </div>
                </FormGroup>
            </Form>
        );
    }

    return (
        <div className={"card-box"}>
            <h4>Add Users</h4>
            <div className="hr-line-dashed"></div>
            {renderForm()}
        </div>
    );
}

const UserRequestControls: React.FC<any> = (isPending: boolean, updateAll: (action: UpdateAllAction) => void) => {

    return (
        <div className={"card-box"}>
            <h4>Queue Actions</h4>
            <div className="hr-line-dashed"></div>
            <div className="row">
                <button className="btn btn-primary col" type="button" style={{ marginRight: '2px' }} disabled={!isPending}
                    onClick={(e: any) => { e.preventDefault(); updateAll(UpdateAllAction.STOP) }} >Stop All Pending
                </button>
                <button className="btn btn-primary col" type="button" style={{ marginRight: '2px' }} disabled={isPending}
                    onClick={(e: any) => { e.preventDefault(); updateAll(UpdateAllAction.SUBMIT) }} >Submit All Pending
                </button>
                <button className="btn btn-warning col" type="button" style={{ marginRight: '2px' }}
                    onClick={(e: any) => { e.preventDefault(); updateAll(UpdateAllAction.RESET) }} >Reset All
                </button>
                <button className="btn btn-danger col" type="button" style={{ marginRight: '2px' }}
                    onClick={(e: any) => { e.preventDefault(); updateAll(UpdateAllAction.REMOVE) }} >Remove All
                </button>

            </div>
        </div>
    )
}

const UserList: React.FC<any> = (users: UserAction[], removeUser: (user: any) => void, isPending?: boolean) => {

    const [visibleItems, setVisibleItems] = useState<UserAction[]>([]);
    const [pager, setPager] = useState(new PagerModel());

    function onPagerChange(pager: PagerModel) {
        setPager(pager);
    }

    function getColumns() {
        return [
            {
                property: "id", name: "Id", orderBy: "id", type: ColumnType.LINK,
                renderer: (property: string, name: string, item: UserAction) => {
                    return <a href={"/user/" + item.id} target="_blank" rel="noreferrer">{item.id}</a>
                }
            },
            { property: "email", name: "Email" },
            {
                property: "status", name: "Status",
                renderer: (property: string, name: string, item: UserAction) => {
                    switch (item.status) {
                        case UpdateActionStatus.PENDING:
                            return <i className="fe-circle" aria-hidden="true"></i>
                        case UpdateActionStatus.OK:
                            return <i className="fe-check-circle" aria-hidden="true"></i>
                        case UpdateActionStatus.FAIL:
                            return <i className="fe-x-circle" aria-hidden="true"></i>
                        case UpdateActionStatus.DENIED:
                            return <i className="fe-alert-circle" aria-hidden="true"></i>
                        default:
                            return ""
                    };
                }
            },
            { property: "description", name: "Description" }
        ];
    }

    function getActions() {
        return [
            { id: "remove", name: "Remove", className: "btn btn-danger", iconClassName: "fa fa-close" }
        ];
    }

    function onAction(item: any, action: string, index: number): Promise<any> | undefined {
        if (action === "remove") {
            if (removeUser) removeUser(item);
        }
        return Promise.resolve();
    }

    useEffect(() => {
        let itemsLenlength = users ? users.length : 0;
        setVisibleItems(itemsLenlength && itemsLenlength > pager.offset ? users.slice(pager.offset, pager.offset + pager.limit) : []);
    }, [users, pager]);

    return (
        <div className={"card-box"}>
            <div className="row">
                <div className="col-sm-2">
                    <h4>User List</h4>
                </div>
            </div>
            <div className="hr-line-dashed"></div>
            <DataTable
                items={visibleItems ? visibleItems : []}
                onPagerChange={onPagerChange.bind(this)}
                setMaxLimit={100}
                columns={getColumns()}
                actions={getActions()}
                onAction={onAction.bind(this)}
                pager={new PagerModel(pager, users ? users.length : 0)}
                loading={isPending ? true : false}
                itemsUpdating={[]}
                nameResource={"Input List"}
            />
        </div>
    );
}

const UserBatchActions: React.FC<{}> = () => {

    const userService: UserService = getContainer().getUserService();
    const userAuthenticationService: UserAuthenticationService = getContainer().getUserAuthenticationService();
    const isPendingRef = useRef(false);

    const [key, setKey] = useState('user-instructions');
    const [isPending, setIsPending] = useState(false);
    const [showConfirmModal, setShowConfirmModal] = useState<{ title?: string, message?: string }>({});
    const [iterationValues, setIterationValues] = useState<UserAction[]>([]);
    const [userPermissions, setUserPermissions] = useState<{ [key: string]: string }[]>([]);

    function updateIterationValues(iterationValues: UserAction[]) {
        setIterationValues(iterationValues);
    }

    async function addIterationValues(users: number[]) {
        let filteredUsers = users.filter((item, index, original) => original.indexOf(item) === index)
            .filter((item, index) => iterationValues.find((user) => user.id === item) === undefined);

        let currentValues: UserAction[] = [];
        let invalidUsers: string[] = [];
        for (let i = 0; i < filteredUsers.length; i++) {
            try {
                const user: User = await getUser(filteredUsers[i] + "");
                currentValues.push({ id: user.id, email: user.email, status: UpdateActionStatus.ADDED, description: "", user: user } as UserAction);
            } catch (error) {
                console.log("Invalid user", error);
                invalidUsers.push(filteredUsers[i] + "");
            }

        }
        console.log("Current Values", currentValues);
        let newIterationValues = [...iterationValues, ...currentValues].sort((a: User, b: User) => { return Number(a.id) - Number(b.id) });
        setIterationValues(newIterationValues);
        if (invalidUsers.length > 0) {
            setShowConfirmModal({
                title: "User Batch Confirmation",
                message: `The following users are invalid and were not be added: ${invalidUsers.join(", ")}`
            });
        }
    }

    async function getUser(id: string): Promise<User> {
        let response = await userService.getEntity(id);
        return response;
    }

    function updateUser(user: any) {
        setIterationValues(iterationValues.map((value: any, index: number) => {
            return (user.id === value.id ? user : value);
        }));
    }

    function removeUser(user: User) {
        let filteredUsers = iterationValues.filter((value: any, index: number) => user.id !== value.id);
        setIterationValues(filteredUsers);
    }

    function resetAll() {
        let newIterationValues = [...iterationValues];
        for (let i = 0; i < newIterationValues.length; i++) {
            newIterationValues[i].status = undefined;
            newIterationValues[i].description = "";
            newIterationValues[i].request = undefined;
        }
        setIterationValues(newIterationValues);
    }

    function removeAll() {
        setIterationValues([]);
    }

    async function submitAll() {
        if (!iterationValues || iterationValues.length < 1) {
            setShowConfirmModal({
                title: "User Batch Confirmation",
                message: "Please fill out the user list."
            });
        } else if (iterationValues[0].status === UpdateActionStatus.ADDED) {
            setShowConfirmModal({
                title: "User Batch Confirmation",
                message: "Please prepare a task before submitting."
            });
        } else {
            setShowConfirmModal({
                title: "User Batch Confirmation",
                message: "Please fill out the user list."
            });
        }
    }

    function updateIsPending(isPending: boolean) {
        isPendingRef.current = isPending;
        setIsPending(isPending);
    }

    function updateAll(action: String) {
        if (action === UpdateAllAction.STOP) {
            console.log("Is Pending", isPendingRef.current);
            updateIsPending(false);
            setShowConfirmModal({});
        }
        if (action === UpdateAllAction.RESET) {
            resetAll();
        }
        if (action === UpdateAllAction.REMOVE) {
            removeAll();
        }
        if (action === UpdateAllAction.SUBMIT) {
            submitAll();
        }
    }

    async function confirmedSubmit(index: number) {
        console.log("isPending: ", isPendingRef.current);
        if (!isPendingRef.current) return;
        if (!iterationValues
            || index > iterationValues.length - 1
            || !iterationValues[index]) {
            if (isPendingRef.current) updateIsPending(false);
            console.log("No more, stop", index);
            return;
        }
        let userRequest = iterationValues[index];
        try {
            console.log("Request: " + index, userRequest);
            //if is paused stop the process
            if (userRequest.status === UpdateActionStatus.OK || !userRequest.request) { console.log("This is done, do next", index); return };
            localStorage.setItem("showRequestDeniedModal", "true");
            let result = await userRequest.request();
            console.log("Result: " + index, result);
            if (result) {
                userRequest.status = result.status;
                userRequest.description = result.description;
                userRequest.response = result.response;
                if (result.status === UpdateActionStatus.DENIED) {
                    setShowConfirmModal({});
                    updateIsPending(false);
                    return;
                }
            } else {
                userRequest.status = UpdateActionStatus.FAIL;
                userRequest.description = `Failed to run request`;
            }
        } catch (error) {
            console.log("Error: " + index, error);
            userRequest.status = UpdateActionStatus.FAIL;
            userRequest.description = `Failed to run request`;
        } finally {
            localStorage.removeItem("showRequestDeniedModal");
            updateUser(userRequest);
            setTimeout(() => {
                console.log("Next: " + (index + 1));
                confirmedSubmit(index + 1);
            }, WAIT_TIME);
        }

    }

    useEffect(() => {
        userAuthenticationService.getGroupPermissions().then((response) => {
            setUserPermissions(response);
        }).catch((error) => {
            console.log("Error", error);
        });

    }, []);

    return (
        <>
            <h1>User Actions</h1>
            <div className="row">
                <div className={"col-lg-6"}>
                    <div className={"row"}>
                        <div className="col-lg-12">
                            {
                                UsersForm(addIterationValues)
                            }
                        </div>
                        <div className="col-lg-12">
                            {
                                UserRequestControls(isPending, updateAll)
                            }
                        </div>
                        <div className="col-lg-12">
                            {
                                UserList(iterationValues, removeUser, isPending)
                            }
                        </div>
                    </div>
                </div>
                <div className={"col-lg-6"}>
                    <Card className="col-lg-12">
                        <Tab.Container activeKey={key} onSelect={setKey}>
                            <Card.Header>
                                <Nav variant="tabs">
                                    <Nav.Item>
                                        <Nav.Link eventKey="user-instructions"><h6>Instructions</h6></Nav.Link>
                                    </Nav.Item>
                                    {userPermissions["USERS"] &&
                                        <Nav.Item>
                                            <Nav.Link eventKey="user-comments"><h6>User Comments</h6></Nav.Link>
                                        </Nav.Item>
                                    }
                                    {userPermissions["USER_LABELS"] &&
                                        <Nav.Item>
                                            <Nav.Link eventKey="user-labels"><h6>User Label</h6></Nav.Link>
                                        </Nav.Item>
                                    }
                                    {userPermissions["MANUAL_BONUS_AWARD"] &&
                                        <Nav.Item>
                                            <Nav.Link eventKey="user-bonus"><h6>User Bonus Award</h6></Nav.Link>
                                        </Nav.Item>
                                    }
                                    {userPermissions["ACCOUNT_ATTRIBUTES"] &&
                                        <Nav.Item>
                                            <Nav.Link eventKey="user-attributes"><h6>User Attributes</h6></Nav.Link>
                                        </Nav.Item>
                                    }
                                    {userPermissions["ENABLED_PRODUCTS"] &&
                                        <Nav.Item>
                                            <Nav.Link eventKey="user-products"><h6>User Products</h6></Nav.Link>
                                        </Nav.Item>
                                    }
                                    {userPermissions["MANUAL_DORMANT_COLLECTION"] &&
                                        <Nav.Item>
                                            <Nav.Link eventKey="user-collections"><h6>Dormant Collection</h6></Nav.Link>
                                        </Nav.Item>
                                    }
                                </Nav>
                            </Card.Header>
                            <Card.Body>
                                <Tab.Content>
                                    <Tab.Pane eventKey="user-instructions">
                                        <div className={"row"}>
                                            <div className="col-lg-12">
                                                <UserActionContainer title={"Instructions"}>
                                                    <span className={"help-block"}>1. Enter a list of users separated by commas. i.e: 1,13,14,15,5</span><br></br>
                                                    <span className={"help-block"}>2. Click on Add Users. Duplicate ids will be removed, the list will be sorted and validated against the database.</span><br></br>
                                                    <span className={"help-block"}>3. Select the Action to perform by clicking on the appropiate menu tab. i.e: User Comments</span><br></br>
                                                    <span className={"help-block"}>4. Fill out the selected form and click on the corresponding action so that the screen prepares the queue.</span><br></br>
                                                    <span className={"help-block"}>5. Click on Submit All Pending and Confirm that pending tasks should be processed.</span><br></br>
                                                    <div className={"hr-line-dashed"}></div>
                                                    <span className={"help-block"}>** The process can be stopped with Stop All pending and resumed with Submit All Pending</span><br></br>
                                                    <span className={"help-block"}>** Failed attempts are not retried</span><br></br>
                                                    <span className={"help-block"}>** If the page is refreshed the list will be lost.</span><br></br>
                                                    <span className={"help-block"}>** Reset All can be used to clean the queue and leave the same list of users ready for the next task.</span><br></br>
                                                </UserActionContainer>
                                            </div>
                                        </div>
                                    </Tab.Pane>
                                </Tab.Content>
                                <Tab.Content>
                                    <Tab.Pane eventKey="user-comments">
                                        <div className={"row"}>
                                            <div className="col-lg-12">
                                                {key === "user-comments" && userPermissions["USERS"] && <UserComment users={iterationValues} updateUsers={updateIterationValues} />}
                                            </div>
                                        </div>
                                    </Tab.Pane>
                                    <Tab.Pane eventKey="user-bonus">
                                        <div className={"row"}>
                                            <div className="col-lg-12">
                                                {key === "user-bonus" && userPermissions["MANUAL_BONUS_AWARD"] && <BonusAward users={iterationValues} updateUsers={updateIterationValues} />}
                                            </div>
                                        </div>
                                    </Tab.Pane>
                                    <Tab.Pane eventKey="user-labels">
                                        <div className={"row"}>
                                            <div className="col-lg-12">
                                                {key === "user-labels" && userPermissions["USER_LABELS"] && <UserLabels users={iterationValues} updateUsers={updateIterationValues} />}
                                            </div>
                                        </div>
                                    </Tab.Pane>
                                    <Tab.Pane eventKey="user-attributes">
                                        <div className={"row"}>
                                            <div className="col-lg-12">
                                                {key === "user-attributes" && userPermissions["ACCOUNT_ATTRIBUTES"] && <UserAttributes users={iterationValues} updateUsers={updateIterationValues} />}
                                            </div>
                                        </div>
                                    </Tab.Pane>
                                    <Tab.Pane eventKey="user-products">
                                        <div className={"row"}>
                                            <div className="col-lg-12">
                                                {key === "user-products" && userPermissions["ENABLED_PRODUCTS"] && <UserProducts users={iterationValues} updateUsers={updateIterationValues} />}
                                            </div>
                                        </div>
                                    </Tab.Pane>
                                    <Tab.Pane eventKey="user-collections">
                                        <div className={"row"}>
                                            <div className="col-lg-12">
                                                {key === "user-collections" && userPermissions["MANUAL_DORMANT_COLLECTION"] && <UserDormantCollection users={iterationValues} updateUsers={updateIterationValues}/>}
                                            </div>
                                        </div>
                                    </Tab.Pane>
                                    {/* 
                                <Tab.Pane eventKey="user-tests">
                                <div className={"row"}>
                                    <div className="col-lg-12">
                                        { key === "user-tests" && <UserTest users={iterationValues} updateUsers={updateIterationValues}/>}
                                    </div>
                                </div> 
                                </Tab.Pane>
                                */}
                                </Tab.Content>
                            </Card.Body>
                        </Tab.Container>
                    </Card>
                </div>
            </div>
            <ConfirmModal
                show={showConfirmModal && showConfirmModal.message ? true : false}
                hideCancelButton={false}
                heading={showConfirmModal && showConfirmModal.title ? showConfirmModal.title : ""}
                description={showConfirmModal && showConfirmModal.message ? showConfirmModal.message : ""}
                okLabel="OK"
                onOk={() => {
                    setShowConfirmModal({});
                    if (iterationValues && iterationValues.length > 0 && iterationValues[0].status !== UpdateActionStatus.ADDED) {
                        //find the first pending and continue
                        let pendingIndex = iterationValues.findIndex((user) => user.status === UpdateActionStatus.PENDING);
                        if (pendingIndex > -1){ updateIsPending(true); setTimeout(() => confirmedSubmit(pendingIndex), WAIT_TIME); }
                    }
                }}
                onCancel={() => setShowConfirmModal({})}
            />
        </>
    );
}

export default withRouter(UserBatchActions);
